import { Tooltip } from "@base-ui-components/react/tooltip"
import cx from "classnames"
import { Info } from "lucide-react"
import { useId, useState } from "react"
import { usePortalContainer } from "../hooks/usePortalContainer"
import { Switch } from "./ui/Switch"

export function AdminCapabilitiesSwitch({
    checked,
    partial = false,
    disabled = false,
    onChange,
}: {
    checked: boolean
    partial?: boolean
    disabled?: boolean
    onChange: (checked: boolean) => void
}) {
    const switchId = useId()
    const descriptionId = useId()
    const portalContainer = usePortalContainer()
    const [helpOpen, setHelpOpen] = useState(false)
    const description =
        (partial ? "Some admin capabilities are enabled. Manage individual permissions in session details. " : "") +
        "Let this conversation manage WildLark projects, tasks, Actions, Q-mode, agents, processes, follow-ups, files, settings, and connected computers. New tools are available next turn; disabling access blocks subsequent calls."

    return (
        <div className="flex shrink-0 items-center text-xs">
            <div
                className={cx(
                    "flex h-7 cursor-pointer items-center gap-2 whitespace-nowrap pl-2 max-[900px]:h-11",
                    checked ? "text-base-content" : "text-muted hover:text-base-content",
                    disabled && "cursor-wait opacity-60"
                )}
            >
                <Switch
                    id={switchId}
                    aria-label="Admin capabilities"
                    aria-describedby={descriptionId}
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(checked) => onChange(checked)}
                    className={{
                        root: "btn h-5 w-9 shrink-0 cursor-pointer rounded-full bg-base-content/25 transition-colors duration-200 motion-reduce:transition-none",
                        thumb: "rounded-full bg-base-100 shadow-sm data-[checked]:translate-x-4 data-[checked]:bg-primary-content duration-200 motion-reduce:transition-none",
                    }}
                />
                <label htmlFor={switchId} className="flex h-full cursor-pointer items-center">
                    Admin capabilities
                </label>
            </div>
            <span id={descriptionId} className="sr-only">
                {description}
            </span>
            <Tooltip.Root
                open={helpOpen}
                onOpenChange={(open, details) => {
                    // A tap opens help; Tooltip otherwise dismisses on trigger presses.
                    if (details.reason !== "trigger-press") setHelpOpen(open)
                }}
                delay={200}
            >
                <Tooltip.Trigger
                    type="button"
                    aria-label="About admin capabilities"
                    aria-describedby={descriptionId}
                    onClick={() => setHelpOpen(true)}
                    className="btn flex h-7 w-7 items-center justify-center text-muted hover:text-base-content max-[900px]:h-11"
                >
                    <Info size={13} aria-hidden="true" />
                </Tooltip.Trigger>
                <Tooltip.Portal container={portalContainer}>
                    <Tooltip.Positioner side="top" sideOffset={6} collisionPadding={8}>
                        <Tooltip.Popup
                            role="tooltip"
                            className="max-w-[min(20rem,calc(100vw-1rem))] border border-border bg-base-200 px-3 py-2 text-xs leading-relaxed text-base-content shadow-lg"
                        >
                            {description}
                        </Tooltip.Popup>
                    </Tooltip.Positioner>
                </Tooltip.Portal>
            </Tooltip.Root>
        </div>
    )
}
